import type { AdminConfigItemDto, JsonValue } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfigRow } from './ConfigRow';
import type { SetConfigOutcome } from '../_lib/useSetConfig';

/**
 * The runtime-config table shell (AUT-131). Renders the filtered ~44-key
 * set as inline-editable rows (key / value / type / changed-by / actions)
 * matching the mockup's structure. The audit-note footer ("editing a key
 * requires TOTP and is written to the audit log") mirrors the mockup's
 * `pager__note`.
 *
 * Pure presentation — the page owns the data fetch, the filters, and the
 * single PROACTIVE step-up dialog/hook (`onSave` + `busy` flow down).
 */
export interface ConfigTableProps {
  items: AdminConfigItemDto[];
  onSave: (variables: { key: string; value: JsonValue }) => Promise<SetConfigOutcome>;
  busy: boolean;
  /** Empty-state node (no rows after filtering / empty list). */
  empty: ReactElement;
}

export const ConfigTable = ({ items, onSave, busy, empty }: ConfigTableProps): ReactElement => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>{empty}</Box>;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '34%' }}>{t('config.columns.key')}</TableCell>
            <TableCell sx={{ width: '26%' }}>{t('config.columns.value')}</TableCell>
            <TableCell>{t('config.columns.type')}</TableCell>
            <TableCell>{t('config.columns.changed')}</TableCell>
            <TableCell align="right">{t('config.columns.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <ConfigRow key={item.key} item={item} onSave={onSave} busy={busy} />
          ))}
        </TableBody>
      </Table>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('config.auditNote', { count: items.length })}
        </Typography>
      </Box>
    </TableContainer>
  );
};
